import React, { useCallback, useEffect, useState } from "react";
import Cookies from "js-cookie";
import SlidePanel from "./SlidePanel";
import { BadgeGrid } from "./Badges";
import { userShareUrl } from "../helpers/slug";

const MAX_BIO = 300;
const when = (unix) => (unix ? new Date(unix * 1000).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : "");

export default function UserProfile({ API_BASE, username, account, ships, onClose, onViewShip, onBioSaved, setMessage }) {
  const [profile, setProfile] = useState(null);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [tab, setTab] = useState("ships");

  useEffect(() => {
    let cancelled = false;
    setProfile(null); setError(""); setEditing(false); setTab("ships");
    fetch(`${API_BASE}/api/profile/${encodeURIComponent(username)}`)
      .then((r) => r.json())
      .then((d) => { if (!cancelled) (d.error ? setError(d.error === "User not found" ? `No contributor called @${username}.` : d.error) : setProfile(d)); })
      .catch(() => !cancelled && setError("Couldn't load this profile."));
    return () => { cancelled = true; };
  }, [API_BASE, username]);

  const isMe = !!(account && profile && account.username.toLowerCase() === profile.username.toLowerCase());

  const startEdit = () => { setDraft(profile.bio || ""); setSaveError(""); setEditing(true); };

  const saveBio = useCallback(async () => {
    setSaving(true); setSaveError("");
    try {
      const res = await fetch(`${API_BASE}/api/update_profile`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session: Cookies.get("session"), bio: draft }),
      });
      const data = await res.json();
      if (data.error) return setSaveError(data.error);
      setProfile((p) => ({ ...p, bio: data.user?.bio || "" }));
      onBioSaved && onBioSaved(data.user?.bio || "");
      setEditing(false);
      setMessage && setMessage("Bio updated.");
    } catch {
      setSaveError("Couldn't save. Please try again.");
    } finally {
      setSaving(false);
    }
  }, [API_BASE, draft, onBioSaved, setMessage]);

  const copyLink = async () => {
    const url = userShareUrl(profile.username);
    try { await navigator.clipboard.writeText(url); setMessage && setMessage("Profile link copied!"); }
    catch { window.prompt("Copy this link:", url); }
  };

  const shipById = (id) => ships.find((s) => Number(s.id) === Number(id));

  return (
    <SlidePanel title="Contributor" subtitle={profile ? `@${profile.username}` : undefined} onClose={onClose} wide>
      {error && <p className="panel-error">{error}</p>}
      {!profile && !error && <p className="panel-muted">Loading…</p>}

      {profile && (
        <>
          <div className="profile-hero">
            {profile.avatar
              ? <img className="profile-avatar" src={profile.avatar} alt="" onError={(e) => (e.currentTarget.style.display = "none")} />
              : <div className="profile-avatar profile-avatar-fallback">{profile.username[0].toUpperCase()}</div>}
            <h3>@{profile.username}</h3>
            <p className="profile-handle">
              {profile.rank ? `Rank #${profile.rank}` : "Not ranked yet"}
              {profile.admin && <span className="admin-pill">Admin</span>}
            </p>
          </div>

          <div className="up-bio">
            {editing ? (
              <>
                <textarea
                  className="up-bio-input"
                  value={draft}
                  maxLength={MAX_BIO}
                  rows={4}
                  autoFocus
                  placeholder="Tell people what you're into — wreck diving, maritime history, mapping…"
                  onChange={(e) => setDraft(e.target.value)}
                />
                <div className="up-bio-foot">
                  <span className={draft.length > MAX_BIO - 20 ? "up-count warn" : "up-count"}>{draft.length}/{MAX_BIO}</span>
                  <div className="up-bio-btns">
                    <button className="panel-link" onClick={() => setEditing(false)} disabled={saving}>Cancel</button>
                    <button className="panel-primary up-save" onClick={saveBio} disabled={saving}>{saving ? "Saving…" : "Save bio"}</button>
                  </div>
                </div>
                {saveError && <p className="panel-error">{saveError}</p>}
              </>
            ) : (
              <>
                {profile.bio
                  ? <p className="up-bio-text">{profile.bio}</p>
                  : <p className="panel-muted up-bio-empty">{isMe ? "You haven't written a bio yet." : "No bio yet."}</p>}
                {isMe && <button className="panel-link" onClick={startEdit}>✎ {profile.bio ? "Edit bio" : "Add a bio"}</button>}
              </>
            )}
          </div>

          <div className="profile-stat-grid">
            <div className="up-stat"><strong>{profile.points}</strong><span>Points</span></div>
            <div className="up-stat"><strong>{profile.ship_count}</strong><span>Ships</span></div>
            <div className="up-stat"><strong>{profile.edit_count}</strong><span>Edits</span></div>
            <div className="up-stat"><strong>{profile.rank ? `#${profile.rank}` : "–"}</strong><span>Rank</span></div>
          </div>

          {profile.badges && <BadgeGrid badges={profile.badges} isMe={isMe} />}

          <div className="panel-tabs">
            <button className={`panel-tab${tab === "ships" ? " active" : ""}`} onClick={() => setTab("ships")}>Submissions<span>{profile.submissions.length}</span></button>
            <button className={`panel-tab${tab === "edits" ? " active" : ""}`} onClick={() => setTab("edits")}>Recent edits<span>{profile.recent_edits.length}</span></button>
          </div>

          {tab === "ships" && (
            profile.submissions.length === 0 ? (
              <p className="panel-muted">No approved ships yet.</p>
            ) : (
              <div className="panel-list">
                {profile.submissions.map((s) => {
                  const ship = shipById(s.id);
                  return (
                    <div key={s.id} className="panel-item">
                      <div className="panel-thumb">
                        {s.image ? <img src={s.image} alt="" loading="lazy" onError={(e) => (e.currentTarget.style.display = "none")} /> : <span>⚓</span>}
                      </div>
                      <div className="panel-item-main">
                        <h3>{s.title || "Untitled"}</h3>
                        <div className="panel-meta">
                          {s.type && s.type !== "unknown" && <span>{s.type}</span>}
                          {s.date && s.date !== "unknown" && <span>{s.date}</span>}
                        </div>
                        {ship && (
                          <div className="panel-row-actions">
                            <button className="panel-link" onClick={() => onViewShip(ship)}>Show on map →</button>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )
          )}

          {tab === "edits" && (
            profile.recent_edits.length === 0 ? (
              <p className="panel-muted">No approved edits yet.</p>
            ) : (
              <div className="panel-list">
                {profile.recent_edits.map((e) => {
                  const ship = shipById(e.ship_id);
                  return (
                    <div key={e.id} className="panel-item">
                      <div className="panel-item-main">
                        <h3>{e.ship_title || "Removed ship"}</h3>
                        <div className="panel-meta">
                          <span>Changed: {e.fields.length ? e.fields.map((f) => f.replace(/_/g, " ")).join(", ") : "details"}</span>
                          {e.created ? <span>{when(e.created)}</span> : null}
                        </div>
                        {ship && (
                          <div className="panel-row-actions">
                            <button className="panel-link" onClick={() => onViewShip(ship)}>Show on map →</button>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )
          )}

          <button className="panel-secondary" onClick={copyLink}>🔗 Copy profile link</button>
        </>
      )}
    </SlidePanel>
  );
}
